import React, { useCallback, useEffect, useState } from 'react'
import { Button, Form, Input, notification, Row, Col, Space, Switch, Tabs, Select, DatePicker, Tooltip, InputNumber } from 'antd'
import ReactQuill from 'react-quill'

import { Page, Panel } from '../../../../../components'
import { BreadcrumbSystem as Breadcrumb, SysPermission } from '../../../../../constants'
import { announcementService } from '../../../../../services'
import { auth, formatter } from '../../../../../util'
import { announcementCalendarDate, recurrenceUnits, announcementStatuses, announcementDate, recurrenceSummary, nextOccurrence } from '../../recurrence'

import 'antd/dist/antd.css'
import 'react-quill/dist/quill.snow.css'
import './styles.css'
import { InfoCircleOutlined } from '@ant-design/icons'

const FormItem = Form.Item
const Option = Select.Option
const TextArea = Input.TextArea
const { TabPane } = Tabs

const AnnouncementEditorModules = Object.freeze({
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
})

const AnnouncementEditorFormats = Object.freeze([
  'header',
  'bold', 'italic', 'underline', 'strike',
  'blockquote',
  'list', 'bullet',
  'link'
])

const TabInfo = Object.freeze({
  1: { key: '1', tab: 'Announcement Info', path: '/info' }
})
const TabInfoList = Object.freeze(Object.values(TabInfo))

/** Rule: Must define breadcrumb in every page **/
const breadcrumb = Object.freeze([
  Breadcrumb.HomeBase,
  Breadcrumb.AnnouncmentBase,
  Breadcrumb.AnnouncmentList,
  Breadcrumb.AnnouncmentEdit
])

function getPageTitle (id, title) {
  return `${isEdit(id) ? 'Edit' : 'Add'} Announcement${title ? ` - ${title}` : ''}`
}

function getTabContent (key, props) {
  if (key === '1') {
    return <AnnouncementInfo {...props} />
  }

  return null
}

function getTabKey (type) {
  const found = TabInfoList.find(({ path }) => path === `/${type}`)

  if (found) {
    return found.key
  }

  return '1'
}

const hasAccess = (permission) => {
  return auth.hasAccess(permission)
}

function isEdit (id) {
  return id !== 'add'
}

function AnnouncementInfo ({ form, item, onFinish, onFinishFailed }) {
  const [loadingDropdown, setLoadingDropdown] = useState(false)
  const [endDatePickerKey, setEndDatePickerKey] = useState(0)
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false)
  const [organisations, setOrganisations] = useState([])
  const [platforms, setPlatforms] = useState([])
  const [selectedEndDate, setSelectedEndDate] = useState()
  const [selectedPlatformId, setSelectedPlatformId] = useState()
  const [selectedStartDate, setSelectedStartDate] = useState()
  const [startDatePickerKey, setStartDatePickerKey] = useState(0)
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false)
  const [selectedTargetType, setSelectedTargetType] = useState()
  const [repeatUnit, setRepeatUnit] = useState('none')
  const [targets, setTargets] = useState([])

  const { getFieldValue, setFieldsValue } = form || {}
  const selectedTarget = targets.find(({ value }) => value === selectedTargetType)
  const showOrganisationDropdown = selectedTargetType === 'include' || selectedTargetType === 'exclude'
  const targetOrganisationsLabel = selectedTarget ? `${selectedTarget.label} Organisations` : 'Organisations'

  useEffect(() => {
    const { platform_id: platformId, target_type: targetType, start_date: startDate, end_date: endDate } = item || {}
    const startDateValue = startDate ? formatter.toMoment(announcementCalendarDate(startDate)).startOf('day') : undefined
    const endDateValue = endDate ? formatter.toMoment(announcementCalendarDate(endDate)).endOf('day') : undefined
    setFieldsValue({
      ...item, start_date: startDateValue, end_date: endDateValue,
      repeat_unit: (item && item.repeat_unit) || 'none',
      repeat_interval: item && item.repeat_interval,
      repeat_count: item && item.repeat_count,
      show_popup: Boolean(item && item.show_popup)
    })
    setRepeatUnit((item && item.repeat_unit) || 'none')
    setSelectedPlatformId(platformId)
    setSelectedTargetType(targetType)
    setSelectedStartDate(startDateValue)
    setSelectedEndDate(endDateValue)
  }, [setFieldsValue, item])

  const changeEndDate = useCallback((endDate) => {
    setSelectedEndDate(endDate)
  }, [])

  const changeEndDatePickerOpen = useCallback((open) => {
    if (open) {
      // Ant Design 4.10 only applies defaultPickerValue when the picker panel mounts.
      setEndDatePickerKey((key) => key + 1)
    }

    setEndDatePickerOpen(open)
  }, [])

  const changePlatform = useCallback((platformId) => {
    setSelectedPlatformId(platformId)
    setFieldsValue({ target_org_ids: [] })
  }, [setFieldsValue])

  const changeStartDate = useCallback((startDate) => {
    setSelectedStartDate(startDate)
  }, [])

  const changeStartDatePickerOpen = useCallback((open) => {
    if (open) {
      // Ant Design 4.10 only applies defaultPickerValue when the picker panel mounts.
      setStartDatePickerKey((key) => key + 1)
    }

    setStartDatePickerOpen(open)
  }, [])

  const disableStartDate = useCallback((startDate) => {
    return selectedEndDate ? startDate.isAfter(selectedEndDate, 'day') : false
  }, [selectedEndDate])

  const disableEndDate = useCallback((endDate) => {
    return selectedStartDate ? endDate.isBefore(selectedStartDate, 'day') : false
  }, [selectedStartDate])

  const changeRepeatUnit = useCallback((unit) => {
    if (unit === 'none') {
      setFieldsValue({ repeat_interval: null, repeat_count: null })
    } else if (repeatUnit === 'none') {
      setFieldsValue({ repeat_interval: 1, repeat_count: 1 })
    }
    setRepeatUnit(unit)
  }, [repeatUnit, setFieldsValue])

  const changeTarget = useCallback((targetType) => {
    setSelectedTargetType(targetType)

    if (targetType === 'all') {
      setFieldsValue({ target_org_ids: null })
    }
  }, [setFieldsValue])

  useEffect(() => {
    let mounted = true

    setLoadingDropdown(true)
    Promise.all([
      announcementService.getDropdownOrganisations(),
      announcementService.getDropdownPlatforms(),
      announcementService.getDropdownTargets()
    ])
      .then(([organisations, platforms, targets]) => {
        if (mounted) {
          if (organisations && Array.isArray(organisations)) {
            setOrganisations(organisations)
          }

          if (platforms && Array.isArray(platforms)) {
            setPlatforms(platforms)
          }

          if (targets && Array.isArray(targets)) {
            const targetType = getFieldValue('target_type')
            setTargets(targets)

            if (!targetType && targets.some(({ value }) => value === 'all')) {
              setFieldsValue({ target_type: 'all' })
              setSelectedTargetType('all')
            } else {
              setSelectedTargetType(targetType)
            }
          }
        }
      })
      .finally(() => {
        if (mounted) {
          setLoadingDropdown(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [getFieldValue, setFieldsValue])

  return (
    <Form
      form={form}
      initialValues={{ show_popup: false, repeat_unit: 'none' }}
      layout='vertical'
      name='announcement'
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Panel title='Target Audience'>
        <Row gutter={16}>
          <Col lg={4}>
            <FormItem
              label='Platform'
              name='platform_id'
              rules={[{ required: true, message: 'Please select a platform' }]}
            >
              <Select
                loading={loadingDropdown}
                onChange={changePlatform}
                placeholder='Select platform'
                style={{ width: '100%' }}
              >
                {platforms.map(({ id, name }) => (
                  <Option key={id} value={id}>
                    {name}
                  </Option>
                ))}
              </Select>
            </FormItem>
          </Col>

          <Col lg={4}>
            <FormItem
              label='Target'
              name='target_type'
              rules={[{ required: true, message: 'Please select a target' }]}
            >
              <Select
                loading={loadingDropdown}
                onChange={changeTarget}
                placeholder='Select target'
                style={{ width: '100%' }}
              >
                {targets.map(({ label, value }) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </FormItem>
          </Col>

          {showOrganisationDropdown && (
            <Col lg={16}>
              <FormItem
                label={targetOrganisationsLabel}
                name='target_org_ids'
                rules={[{ required: true, message: 'Please select at least one organisation' }]}
              >
                <Select
                  allowClear
                  disabled={!selectedPlatformId}
                  loading={loadingDropdown}
                  mode='multiple'
                  placeholder={
                    selectedPlatformId
                      ? 'Select organisations'
                      : 'Select a platform first'
                  }
                  style={{ width: '100%' }}
                >
                  {organisations
                    .filter(({ platform_id: platformId }) => platformId === selectedPlatformId)
                    .map(({ org_id: orgId, trade_name: tradeName }) => (
                      <Option key={orgId} value={orgId}>{tradeName}</Option>
                    ))}
                </Select>
              </FormItem>
            </Col>
          )}
        </Row>
      </Panel>

      <Panel title='Basic Info'>
        <Row gutter={16}>
          <Col lg={4}>
            <FormItem
              label='Start Date'
              name='start_date'
              dependencies={['end_date', 'repeat_unit']}
              rules={[({ getFieldValue }) => ({
                validator (_, value) {
                  if (!value && (getFieldValue('end_date') || getFieldValue('repeat_unit') !== 'none')) {
                    return Promise.reject(new Error('Please select a start date'))
                  }

                  return Promise.resolve()
                }
              })]}
            >
              <DatePicker
                allowClear
                defaultPickerValue={
                  selectedEndDate
                    ? selectedEndDate.clone().startOf('day')
                    : formatter.toMoment().startOf('day')
                }
                disabledDate={disableStartDate}
                key={startDatePickerKey}
                onChange={changeStartDate}
                onOpenChange={changeStartDatePickerOpen}
                open={startDatePickerOpen}
                style={{ width: '100%' }}
              />
            </FormItem>
          </Col>

          <Col lg={4}>
            <FormItem
              label='End Date'
              name='end_date'
              dependencies={['start_date', 'repeat_unit']}
              rules={[({ getFieldValue }) => ({
                validator (_, value) {
                  if (!value && getFieldValue('repeat_unit') !== 'none') {
                    return Promise.reject(new Error('Recurrence requires an end date'))
                  }
                  const start = getFieldValue('start_date')
                  if (value && start && value.isBefore(start, 'day')) {
                    return Promise.reject(new Error('End date must be on or after start date'))
                  }
                  return Promise.resolve()
                }
              })]}
            >
              <DatePicker
                allowClear
                defaultPickerValue={
                  selectedStartDate
                    ? selectedStartDate.clone().endOf('day')
                    : formatter.toMoment().endOf('day')
                }
                disabledDate={disableEndDate}
                key={endDatePickerKey}
                onChange={changeEndDate}
                onOpenChange={changeEndDatePickerOpen}
                open={endDatePickerOpen}
                style={{ width: '100%' }}
              />
            </FormItem>
          </Col>

          <Col lg={4}>
            <FormItem
              label={
                <>
                  Show Popup&nbsp;
                  <Tooltip title='Will show as a popup that blocks the whole screen'><InfoCircleOutlined /></Tooltip>
                </>
              }
              name='show_popup'
              valuePropName='checked'
            >
              <Switch checkedChildren='Yes' unCheckedChildren='No' />
            </FormItem>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} lg={8}>
            <FormItem label='Recurrence' name='repeat_unit'>
              <Select onChange={changeRepeatUnit}>
                {recurrenceUnits.map(({ value, label }) => <Option key={value} value={value}>{label}</Option>)}
              </Select>
            </FormItem>
          </Col>
          {repeatUnit !== 'none' && (
            <>
              <Col xs={24} lg={8}>
                <FormItem
                  label='Gap interval'
                  name='repeat_interval'
                  extra={`Full ${repeatUnit}s to wait after each occurrence ends.`}
                  rules={[{ required: true, type: 'integer', min: 1, max: 100, message: 'Enter a whole number from 1 to 100' }]}
                >
                  <InputNumber min={1} max={100} step={1} style={{ width: '100%' }} />
                </FormItem>
              </Col>
              <Col xs={24} lg={8}>
                <FormItem
                  label='Total occurrences'
                  name='repeat_count'
                  extra='Includes the original occurrence. 1 means the original only.'
                  rules={[{ required: true, type: 'integer', min: 1, max: 100, message: 'Enter a whole number from 1 to 100' }]}
                >
                  <InputNumber min={1} max={100} step={1} style={{ width: '100%' }} />
                </FormItem>
              </Col>
            </>
          )}
        </Row>
        <p>Dates are inclusive and use Melbourne time. Each occurrence keeps the original number of calendar days.</p>
        {item && item.id && (
          <div>
            <strong>Saved schedule: {recurrenceSummary(item)}</strong>
            <p>
              Status: {announcementStatuses[item.status] || item.status}
              {' · '}Final end: {announcementDate(item.schedule_end_date)}
              {' · '}Next occurrence: {nextOccurrence(item)}
            </p>
          </div>
        )}

        <Row gutter={16}>
          <Col lg={8}>
            <FormItem
              label='Title'
              name='title'
              rules={[
                { required: true, message: 'Please enter a title' },
                { whitespace: true, message: 'Please enter a title' }
              ]}
            >
              <Input style={{ width: '100%' }} />
            </FormItem>
          </Col>

          <Col lg={16}>
            <FormItem
              label='Short Description'
              name='short_description'
              rules={[
                { required: true, message: 'Please enter a short description' },
                { whitespace: true, message: 'Please enter a short description' }
              ]}
            >
              <TextArea
                autoSize={{ maxRows: 5, minRows: 1 }}
                style={{ width: '100%' }}
              />
            </FormItem>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col lg={24}>
            <FormItem label='Long Description' name='long_description'>
              <ReactQuill
                className='announcement-long-description'
                formats={AnnouncementEditorFormats}
                modules={AnnouncementEditorModules}
                theme='snow'
              />
            </FormItem>
          </Col>
        </Row>
      </Panel>
    </Form>
  )
}

function AnnouncementPage (props) {
  const { history, match } = props || {}
  const [form] = Form.useForm()

  const [item, setItem] = useState({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const { resetFields } = form || {}
  const { title } = item || {}
  const { params } = match || {}
  const { id: pId, type: pType } = params || {}
  const tabKey = getTabKey(pType)

  useEffect(() => {
    let mounted = true

    if (!isEdit(pId) || !hasAccess(SysPermission.ORGANISATIONS.INFO.READ)) {
      setItem({})
      resetFields()
      return
    }

    setLoading(true)
    announcementService.get(pId)
      .then((response) => {
        if (!mounted) {
          return
        }

        if (response && response.id) {
          setItem(response)
        } else {
          notification.error({
            message: 'Load Announcement not successful',
            description: 'Announcement details could not be loaded.'
          })
        }
      })
      .catch(() => {
        if (mounted) {
          notification.error({
            message: 'Load Announcement not successful',
            description: 'Announcement details could not be loaded.'
          })
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [resetFields, pId])

  const saveAnnouncement = useCallback(async (values) => {
    setSaving(true)
    const payload = {
      ...values,
      start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
      end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
      repeat_interval: values.repeat_unit === 'none' ? null : values.repeat_interval,
      repeat_count: values.repeat_unit === 'none' ? null : values.repeat_count,
      show_popup: Boolean(values.show_popup),
      target_org_ids: values.target_type === 'all' ? null : values.target_org_ids
    }

    try {
      const response = isEdit(pId)
        ? await announcementService.save(pId, payload)
        : await announcementService.add(payload)

      if (response && response.errors) {
        form.setFields(Object.entries(response.errors).map(([name, error]) => ({ name, errors: [error] })))
        notification.error({ message: 'Save Announcement not successful', description: response.message })
        return
      }
      if (!response || !response.id) {
        notification.error({
          message: 'Save Announcement not successful',
          description: 'Announcement could not be saved.'
        })
        return
      }

      setItem((currentItem) => ({ ...currentItem, ...payload, ...response }))
      notification.success({
        message: `${isEdit(pId) ? 'Update' : 'Create'} Announcement successfully`,
        description: `Announcement was ${isEdit(pId) ? 'updated' : 'created'} successfully.`
      })

      if (!isEdit(pId)) {
        history.replace(`/announcements/listing/${response.id}/info`)
      }
    } catch (error) {
      notification.error({
        message: 'Save Announcement not successful',
        description: 'Announcement could not be saved.'
      })
    } finally {
      setSaving(false)
    }
  }, [form, history, pId])

  const saveAnnouncementFailed = useCallback(() => {
    notification.error({
      message: 'Save Announcement not successful',
      description: 'Please correct the highlighted fields and try again.'
    })
  }, [])

  const changeTab = useCallback((tabKey) => {
    const tab = TabInfo[tabKey]

    if (tab) {
      history.push(`/announcements/listing/${pId}${tab.path}`)
    }
  }, [history, pId])

  return (
    <Page.Body>
      <Page.Breadcrumb item={breadcrumb} />

      <Page.Header title={getPageTitle(pId, title)}>
        <Space>
          {tabKey === '1' && (hasAccess(SysPermission.ORGANISATIONS.INFO.UPDATE) || hasAccess(SysPermission.ORGANISATIONS.INFO.CREATE)) ? (
            <Button
              loading={saving}
              onClick={form.submit}
              shape='round'
              type='primary'
            >
              {saving ? 'Saving' : 'Save'}
            </Button>
          ) : null}

          <Button ghost shape='round' type='primary' onClick={history.goBack}>
            Back
          </Button>
        </Space>
      </Page.Header>

      <Page.ContentLoading isLoading={loading} isUpdate={saving}>
        <Tabs defaultActiveKey={tabKey} onChange={changeTab}>
          {TabInfoList.map(({ key, tab }) => (
            <TabPane key={key} tab={tab}>
              {getTabContent(key, {
                form,
                item,
                onFinish: saveAnnouncement,
                onFinishFailed: saveAnnouncementFailed
              })}
            </TabPane>
          ))}
        </Tabs>
      </Page.ContentLoading>
    </Page.Body>
  )
}

export default AnnouncementPage
